import type {
  WorkflowStep,
  WorkflowTriggerType,
  WorkflowWithStats,
} from "@/features/workflow/types";

import { MOCK_AD_CAMPAIGNS } from "./ads";
import { createId, createPastTimestamp, randomInt } from "./base";

interface WorkflowSeed {
  /** Optional stable id so tests/screenshots can deep-link the edit canvas. */
  id?: string;
  name: string;
  description: string;
  triggerType: WorkflowTriggerType;
  isActive: boolean;
  /** Optional pre-built steps so the edit canvas renders a populated flow. */
  buildSteps?: () => WorkflowStep[];
}

/**
 * A populated flow: conversation_opened → ask_question → send_message, so the
 * edit page (/workflows/:id) shows a real canvas in mock/test mode.
 */
function buildWelcomeSteps(): WorkflowStep[] {
  const triggerId = "step_trigger";
  const askId = "step_ask";
  const sendId = "step_send";

  return [
    {
      id: triggerId,
      type: "trigger",
      nodeType: "conversation_opened",
      label: "Conversation Opened",
      config: { source: ["contact"] },
      position: { x: 0, y: 0 },
      next: [askId],
    },
    {
      id: askId,
      type: "action",
      nodeType: "ask_question",
      label: "Ask a Question",
      config: {
        body: "Hi! What brings you in today?",
        questionType: "text",
        useLastActiveChannel: true,
        waitForContactReply: true,
        saveResponseAs: "Reason for visit",
      },
      position: { x: 0, y: 160 },
      next: [sendId],
    },
    {
      id: sendId,
      type: "action",
      nodeType: "send_message",
      label: "Send Message",
      config: {
        body: "Thanks! One of our team will be right with you.",
        useLastActiveChannel: true,
      },
      position: { x: 0, y: 320 },
    },
  ];
}

const WORKFLOW_SEEDS: WorkflowSeed[] = [
  {
    id: "wf-seed-consultation-follow-up",
    name: "Consultation follow-up",
    description: "Start a follow-up for a patient after their consultation.",
    triggerType: "manual_trigger",
    isActive: true,
    buildSteps: () => [
      {
        id: "manual",
        type: "trigger",
        nodeType: "manual_trigger",
        label: "Start manually",
        config: {},
        position: { x: 0, y: 0 },
        next: ["follow-up"],
      },
      {
        id: "follow-up",
        type: "action",
        nodeType: "create_follow_up",
        label: "Confirm the consultation outcome",
        config: {
          reason: "Confirm the consultation outcome",
          remind_at_direction: "after",
          remind_at_duration: 1,
          remind_at_unit: "days",
        },
        position: { x: 0, y: 160 },
      },
    ],
  },
  {
    id: "wf-seed-welcome",
    name: "New Lead Welcome",
    description: "Greets new leads and books a consultation.",
    triggerType: "conversation_opened",
    isActive: true,
    buildSteps: buildWelcomeSteps,
  },
  {
    name: "Appointment Reminder",
    description: "Sends a reminder a day before the appointment.",
    triggerType: "appointment_status_update",
    isActive: true,
  },
  {
    name: "Post-Treatment Follow-up",
    description: "Checks in with patients after a treatment.",
    triggerType: "contact_lifecycle_change",
    isActive: false,
  },
  {
    id: "wf-seed-ad",
    name: "Ad Click Router",
    description: "Routes leads by the campaign they clicked.",
    triggerType: "click_to_chat_ad",
    isActive: true,
    buildSteps: buildAdTriggerSteps,
  },
  {
    name: "Re-engage Cold Leads",
    description: "Win-back message for idle conversations.",
    triggerType: "idle",
    isActive: true,
  },
];

const CAMPAIGN_CONDITIONS: Record<string, unknown> = {
  source: "click_to_chat_ad",
  campaignIds: [MOCK_AD_CAMPAIGNS.get("Clinic awareness campaign")!.id],
};

function buildAdTriggerSteps(): WorkflowStep[] {
  return [
    {
      id: "step_trigger",
      type: "trigger",
      nodeType: "click_to_chat_ad",
      label: "Click to Chat Ad",
      config: CAMPAIGN_CONDITIONS,
      position: { x: 0, y: 0 },
      next: [],
    },
  ];
}

const buildConditions = (seed: WorkflowSeed): Record<string, unknown> => {
  if (seed.triggerType === "click_to_chat_ad") return CAMPAIGN_CONDITIONS;

  return (
    seed.buildSteps?.().find((step) => step.type === "trigger")?.config ?? {}
  );
};

export function generateWorkflows(
  organizationId: string,
  workspaceId: string,
): WorkflowWithStats[] {
  return WORKFLOW_SEEDS.map((seed) => {
    const runs = randomInt(20, 800);
    const successRate = randomInt(70, 99);

    return {
      id: seed.id ?? createId(),
      name: seed.name,
      description: seed.description,
      steps: seed.buildSteps?.() ?? [],
      triggerStrategy: {
        type: seed.triggerType,
        conditions: buildConditions(seed),
      },
      exitStrategy: null,
      isActive: seed.isActive,
      validationErrors: null,
      createdBy: null,
      organizationId,
      workspaceId,
      createdAt: createPastTimestamp(90),
      updatedAt: createPastTimestamp(5),
      deletedAt: null,
      runs,
      successRate,
      lastTriggeredAt: seed.isActive ? createPastTimestamp(2) : null,
      dailyRuns: Array.from({ length: 7 }, () => randomInt(0, 40)),
    };
  });
}
